import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, Icon } from 'semantic-ui-react';
import { tl } from 'common/services/i18nService';
import { RootState } from '../../../store/rootReducer';
import { closeAiSupportedMarkingIntro, setDontShowAgain } from '../actions';
import { getLoggedInUserId, writeDismissedForWorkspaceVisit } from '../dontShowAgainPreference';
import { isAiIntroKnownReadOnly, shouldHideAiIntroForReadOnly } from '../introReadOnly';
import { AiSupportedMarkingIntroState } from '../reducer';
import * as styles from './AiSupportedMarkingIntroModal.scss';
import aiSupportedMarkingIntroIcon from './aiSupportedMarkingIntroIcon.png';

const AiSupportedMarkingIntroModal: React.FC = () => {
    const dispatch = useDispatch();

    const { open, dontShowAgain } = useSelector<RootState, AiSupportedMarkingIntroState>(
        (state: any) => state.aiSupportedMarkingIntro || {
            open: false,
            dontShowAgain: false,
        }
    );
    const userId = getLoggedInUserId();
    const assessmentRunId = useSelector<RootState, number | undefined>((state: any) => state.context?.assessmentRunId);
    const gradingPartyDocument = useSelector((state: any) => state.context?.partyState?.gradingPartyDocument);
    const hideForReadOnly = shouldHideAiIntroForReadOnly(gradingPartyDocument);

    React.useEffect(() => {
        if (open && isAiIntroKnownReadOnly(gradingPartyDocument)) {
            dispatch(closeAiSupportedMarkingIntro());
        }
    }, [open, gradingPartyDocument, dispatch]);

    const dismissForWorkspaceVisit = () => {
        writeDismissedForWorkspaceVisit(userId, assessmentRunId);
        dispatch(closeAiSupportedMarkingIntro());
    };

    const onConfirm = dismissForWorkspaceVisit;

    const onDontShowAgainChange = () => {
        dispatch(setDontShowAgain(!dontShowAgain, userId, assessmentRunId));
    };

    return (
        <Modal
            open={open && !hideForReadOnly}
            closeOnEscape={false}
            closeOnDimmerClick={false}
            className={styles.aiMarkingModal}
        >
            <Modal.Header>
                <div className={styles.headerTitle}>
                    <img
                        src={aiSupportedMarkingIntroIcon}
                        alt=""
                        className={styles.headerIcon}
                    />
                    <span>{tl('grading.aiMarking.intro.title')}</span>
                </div>

                <p className={styles.intro}>{tl('grading.aiMarking.intro.description')}</p>
            </Modal.Header>

            <Modal.Content>
                {/* ── Frame 1 — Mark manually first ── */}
                <div className={`${styles.frame} ${styles.frame1}`}>
                    <div className={styles.row}>
                        <div className={styles.bulletWrapper}>
                            <div className={styles.bullet}>
                                <span>1</span>
                            </div>
                        </div>

                        <div className={styles.textCol}>
                            <div className={styles.titleRow}>
                                <strong className={styles.stepTitle}>{tl('grading.aiMarking.intro.step1.title')}</strong>
                            </div>

                            <div className={styles.paraWrapper}>
                                <p
                                    className={styles.paraText}
                                    dangerouslySetInnerHTML={{ __html: tl('grading.aiMarking.intro.step1.text') }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.infoWrapper}>
                        <div className={`${styles.info} ${styles.info1}`}>
                            <Icon name="info circle" className={styles.infoIcon} />

                            <div className={`${styles.infoTextFrame} ${styles.infoTextFrame1}`}>
                                <span className={styles.infoHeading}>{tl('grading.aiMarking.intro.info.label')}</span>
                                <span className={`${styles.infoValue} ${styles.infoValue1}`}>
                                    {tl('grading.aiMarking.intro.step1.info')}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Frame 2 — AI builds confidence ── */}
                <div className={`${styles.frame} ${styles.frame2}`}>
                    <div className={styles.row}>
                        <div className={styles.bulletWrapper}>
                            <div className={styles.bullet}>
                                <span>2</span>
                            </div>
                        </div>

                        <div className={styles.textCol}>
                            <div className={styles.titleRow}>
                                <strong className={styles.stepTitle}>{tl('grading.aiMarking.intro.step2.title')}</strong>
                            </div>

                            <div className={styles.paraWrapper}>
                                <p className={styles.paraText}>
                                    {tl('grading.aiMarking.intro.step2.text')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Frame 3 — Review AI suggestion ── */}
                <div className={`${styles.frame} ${styles.frame3}`}>
                    <div className={styles.row}>
                        <div className={styles.bulletWrapper}>
                            <div className={styles.bullet}>
                                <span>3</span>
                            </div>
                        </div>

                        <div className={styles.textCol}>
                            <div className={styles.titleRow}>
                                <strong className={styles.stepTitle}>{tl('grading.aiMarking.intro.step3.title')}</strong>
                            </div>

                            <div className={styles.paraWrapper}>
                                <p className={styles.paraText}>
                                    {tl('grading.aiMarking.intro.step3.text')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Content>

            <Modal.Actions>
                <label
                    htmlFor="aiMarkingDontShowAgain"
                    className={styles.checkboxLabel}
                >
                    <input
                        id="aiMarkingDontShowAgain"
                        type="checkbox"
                        checked={dontShowAgain}
                        onChange={onDontShowAgainChange}
                        className={styles.checkbox}
                    />
                    {tl('grading.aiMarking.intro.dontShowAgain')}
                </label>

                <button
                    type="button"
                    className={styles.startBtn}
                    onClick={onConfirm}
                >
                    <span>{tl('grading.aiMarking.intro.startMarking')}</span>
                </button>
            </Modal.Actions>
        </Modal>
    );
};

export default AiSupportedMarkingIntroModal;
